import { useCallback } from "react"
import { Address } from "viem"
import { useAccount } from "wagmi"

import { MEZO_CHAIN } from "#/shared/constants"
import { Router } from "#/shared/constants/artifacts"
import {
  getSwapDeadline,
  getSwapMinAmountOut,
  getSwapRoutes,
} from "#/shared/utils"

import { useWriteContractAndWaitForTransaction } from "../contract"

type SwapTokenProps = {
  tokenIn: Address
  tokenOut: Address
  amountIn: bigint
  amountOut: bigint
  stable: boolean
  slippage: number
}

const useSwapTokens = () => {
  const { address } = useAccount()

  const {
    writeContractAsync,
    isPending: isSwapTransactionPending,
    isTxConfirmed: isSwapTransactionConfirmed,
    isWaitingForTxReceipt: isWaitingForSwapConfirmation,
    isError: isSwapError,
  } = useWriteContractAndWaitForTransaction(MEZO_CHAIN.id)

  const swapTokensHandler = useCallback(
    async ({
      tokenIn,
      tokenOut,
      amountIn,
      amountOut,
      slippage,
      stable,
    }: SwapTokenProps): Promise<`0x${string}` | null> => {
      const transactionHash = await writeContractAsync({
        abi: Router.abi,
        address: Router.address,
        functionName: "swapExactTokensForTokens",
        args: [
          amountIn,
          getSwapMinAmountOut(amountOut, slippage),
          getSwapRoutes(tokenIn, tokenOut, stable),
          address,
          getSwapDeadline(),
        ],
      })

      return transactionHash
    },
    [writeContractAsync, address],
  )

  return {
    swapTokens: swapTokensHandler,
    isSwapTransactionPending,
    isWaitingForSwapConfirmation,
    isSwapTransactionConfirmed,
    isSwapError,
  }
}

export default useSwapTokens
